import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import CardHeader from "@mui/material/CardHeader";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import classnames from "classnames";
import order from "lodash/orderBy";

import usePriceFormat from "../hooks/use-price-format.hook";
import useTranslate from "../hooks/use-translate.hook";
import { Chart, cssUtils, flex } from "../ui";
import { Table } from "../ui/table";
import { isLoadingResults, getResults } from "./report.selectors";
import Filters from "./filters.component";

const ProductsSoldByFunnel = ({ initialDate, finalDate }) => {
    const t = useTranslate();
    const priceFormat = usePriceFormat();

    const loading = useSelector( isLoadingResults );
    const results = useSelector( getResults );

    const [ teamId, setTeamId ] = useState();
    const [ usersId, setUsersId ] = useState( [] );
    const [ orderBy, setOrderBy ] = useState( "product" );
    const [ orderType, setOrderType ] = useState( "ASC" );

    const handleChange = ( name, value ) => {
        switch ( name ) {
            case "teamId":
                setTeamId( value );
                break;
            case "usersId":
                setUsersId( value );
                setTeamId( null );
                break;
            default:
                break;
        }
    };
    const changeOrder = ({ orderBy, orderType }) => {
        setOrderBy( orderBy );
        setOrderType( orderType );
    };

    const orderedResults = useMemo( () => (
        order( ( results || [] ), [ orderBy ], [ orderType.toLowerCase() ] )
    ), [ results, orderBy, orderType ] );

    const chartData = useMemo( () => ({
        title: {
            text: " "
        },
        xAxis: {
            categories: orderedResults.map( item => item.product ),
            crosshair: true
        },
        yAxis: {
            min: 0,
            title: {
                text: ""
            }
        },
        tooltip: {
            headerFormat: "<span style=\"font-size:10px\">{point.key}</span><table>",
            pointFormat: "<tr><td style=\"color:{series.color};padding:0\">{series.name}: </td>" +
            "<td style=\"padding:0\"><b>{point.y}</b></td></tr>",
            footerFormat: "</table>",
            shared: true,
            useHTML: true
        },
        plotOptions: {
            column: {
                pointPadding: 0.2,
                borderWidth: 0
            }
        },
        legend: {
            enabled: false
        },
        series: [{
            name: t( "report:products-sold-by-funnel.total" ),
            data: orderedResults.map( item => item.quantity ) || 0,
            color: "#4CAF50"
        }]
    }), [ t, orderedResults ] );

    const source = useMemo( () => orderedResults.map( item => ({
        product: item.product + "",
        quantity: item.quantity + "",
        unitPrice: priceFormat( item.unitPrice ) + "",
        totalPrice: priceFormat( item.totalPrice ) + ""
    })), [ priceFormat, orderedResults ] );

    const footer = useMemo( () => {
        const data = ( results || [] ).reduce(( acc, item ) => {
            acc.product = t( "report:products-sold-by-funnel.total" );
            acc.quantity += item.quantity;
            acc.unitPrice += item.unitPrice;
            acc.totalPrice += item.totalPrice;
            return acc;
        }, {
            product: t( "report:products-sold-by-funnel.total" ),
            quantity: 0,
            unitPrice: 0,
            totalPrice: 0
        });

        data.unitPrice = priceFormat( data.unitPrice );
        data.totalPrice = priceFormat( data.totalPrice );

        return data;
    }, [ t, priceFormat, results ] );

    const model = {
        product: t( "report:products-sold-by-funnel.product" ),
        quantity: t( "report:products-sold-by-funnel.quantity" ),
        unitPrice: t( "report:products-sold-by-funnel.unit-price" ),
        totalPrice: t( "report:products-sold-by-funnel.total-price" )
    };

    return (
        <div className={ cssUtils.marginTopSmall }>
            <CardHeader
                title={ t( "report:reports.PRODUCTS_SOLD_BY_FUNNEL" ) }
                subheader={ t( "report:products-sold-by-funnel.help" ) }
            />

            <Paper
                className={ classnames(
                    flex.container, flex.alignItemsCenter, cssUtils.paddingSmall, cssUtils.marginBottomSmall
                )}
            >
                <Filters
                    items={[ "funnels", "users" ]}
                    required={ [ "funnels" ] }
                    onChange={ handleChange }
                    report={ "PRODUCTS_SOLD_BY_FUNNEL" }
                    initialDate={ initialDate.toISOString() }
                    finalDate={ finalDate.toISOString() }
                    teamId={ teamId }
                    usersId={ usersId }
                />
            </Paper>

            <div className={ classnames( flex.container, flex.alignItemsStart ) }>
                <Paper>
                    {
                        results &&
                            <Chart
                                id="productsSoldByFunnelChart"
                                data={ chartData }
                                noDataMessage={ t( "report:empty-results" ) }
                                type="column"
                            />
                    }
                </Paper>
                {
                    loading ?
                        <LinearProgress className={ flex.flexGrow }/> :
                        results &&
                            <div className={ classnames( flex.fill, cssUtils.marginLeftSmall ) }>
                                <Table
                                    id="productsSoldByFunnelTable"
                                    allowExport
                                    model={ model }
                                    source={ source }
                                    footer={ footer }
                                    title={ t( "report:reports.PRODUCTS_SOLD_BY_FUNNEL" ) }
                                    allowOrder
                                    onChangePage={ changeOrder }
                                    orderBy={ orderBy }
                                    orderType={ orderType }
                                />
                            </div>
                }
            </div>
        </div>
    );
};

export default ProductsSoldByFunnel;